import { FileTypeEnum } from '$/utils/upload';
import type { MagicSRParams, VideoHdParams } from '@/domains/tools/types';
import { ImageHdType, VideoSRMode } from '@/store/useAIEnhanceStore';
import { normalizeRequestVideoDuration } from '../../ai_eraser/utils/mediaCheckAfterUpload';

/** 高清模式默认缩放倍数 */
const HD_DEFAULT_SCALE_PARAM = 2.0;
/** 高清模式默认最长边 */
const HD_DEFAULT_MAX_SIDE_SIZE = 4096;
/** 超清模式默认放大倍数 */
const SUPER_DEFAULT_SR_NUM = 4;
/** 老照片默认上色模式 */
const OLD_PHOTO_DEFAULT_COLOR_MODE = 1;
/** 视频 4K 档位 */
const VIDEO_4K_SR_MODE = 3;
/** 全能修复 2K 档位 */
const VIDEO_ALL_IN_ONE_2K_SR_MODE = 2;

export type AIEnhanceTaskParams = MagicSRParams | VideoHdParams;

export type AIEnhanceTaskPayload =
  | { taskCategory: 'magicsr'; params: MagicSRParams; }
  | { taskCategory: 'video_hd'; params: VideoHdParams; };

/**
 * 根据视频高清模式补充算法需要的 sr_mode。
 */
function getVideoHdSRMode(videoSRMode?: VideoSRMode): 2 | 3 | undefined {
  if (videoSRMode === VideoSRMode._2K) return VIDEO_ALL_IN_ONE_2K_SR_MODE;
  if (videoSRMode === VideoSRMode._4K || videoSRMode === VideoSRMode.AI4KPlus) return VIDEO_4K_SR_MODE;
  return undefined;
}

/**
 * 根据视频高清类型组装 video_hd 任务参数。
 */
function buildVideoHdParams(imageFile: string, videoSRMode: VideoSRMode = VideoSRMode._2K, videoDuration = 0): VideoHdParams {
  const srMode = getVideoHdSRMode(videoSRMode);
  return {
    init_image: imageFile,
    hd_type: videoSRMode,
    video_duration: normalizeRequestVideoDuration(videoDuration),
    ...(srMode ? { sr_mode: srMode } : {}),
  };
}

/**
 * 根据图片高清类型组装 magicsr 任务参数。
 */
function buildImageMagicSRParams(imageFile: string, imageHdType: ImageHdType = ImageHdType.HD): MagicSRParams {
  const base: MagicSRParams = {
    image_file: imageFile,
    hd_type: imageHdType,
  };

  if (imageHdType === ImageHdType.HD) {
    return {
      ...base,
      scale_param: HD_DEFAULT_SCALE_PARAM,
      max_side_size_param: HD_DEFAULT_MAX_SIDE_SIZE,
    };
  }

  if (imageHdType === ImageHdType.Super) {
    return {
      ...base,
      sr_num: SUPER_DEFAULT_SR_NUM,
    };
  }

  if (imageHdType === ImageHdType.OldPicture) {
    return {
      ...base,
      old_photo_color_mode: OLD_PHOTO_DEFAULT_COLOR_MODE,
    };
  }

  return base;
}

/**
 * 根据媒体类型组装 AI 变高清任务分类和对应参数。
 */
export function buildAIEnhanceTaskPayload(options: {
  imageFile: string;
  imageHdType?: ImageHdType;
  videoSRMode?: VideoSRMode;
  mediaType?: FileTypeEnum | 'image' | 'video';
  videoDuration?: number;
}): AIEnhanceTaskPayload {
  const {
    imageFile,
    imageHdType = ImageHdType.HD,
    videoSRMode,
    mediaType,
    videoDuration = 0,
  } = options;

  const isVideo = mediaType === FileTypeEnum.Video || mediaType === 'video';
  if (isVideo) {
    return {
      taskCategory: 'video_hd',
      params: buildVideoHdParams(imageFile, videoSRMode, videoDuration),
    };
  }

  return {
    taskCategory: 'magicsr',
    params: buildImageMagicSRParams(imageFile, imageHdType),
  };
}

/**
 * 兼容旧调用：只返回对应任务参数。
 */
export function buildMagicSRParams(options: Parameters<typeof buildAIEnhanceTaskPayload>[0]): AIEnhanceTaskParams {
  return buildAIEnhanceTaskPayload(options).params;
}
